<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import MailingListsAPI from 'dashboard/api/mailingLists';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const route = useRoute();
const { t } = useI18n();

const lists = ref([]);
const selectedList = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const form = reactive({
  name: '',
  description: '',
  terms: '',
});

const isEditing = computed(() => !!selectedList.value);
const sortedLists = computed(() =>
  [...lists.value].sort((a, b) => a.name.localeCompare(b.name))
);

const emailCampaignsRoute = computed(() => ({
  name: 'email_marketing_index',
  params: { accountId: route.params.accountId },
}));

const resetForm = () => {
  selectedList.value = null;
  form.name = '';
  form.description = '';
  form.terms = '';
  errorMessage.value = '';
  successMessage.value = '';
};

const listPayload = () => ({
  mailing_list: {
    name: form.name.trim(),
    description: form.description.trim(),
    terms: form.terms.trim(),
    status: selectedList.value?.status || 'active',
  },
});

const fetchLists = async () => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await MailingListsAPI.get();
    lists.value = response.data?.payload || [];
  } catch (error) {
    errorMessage.value = t('CAMPAIGN.EMAIL.MAILING_LISTS.ERROR_LOAD');
  } finally {
    isLoading.value = false;
  }
};

const selectList = mailingList => {
  selectedList.value = mailingList;
  form.name = mailingList.name || '';
  form.description = mailingList.description || '';
  form.terms = mailingList.terms || '';
  errorMessage.value = '';
  successMessage.value = '';
};

const saveList = async () => {
  if (!form.name.trim()) {
    errorMessage.value = t('CAMPAIGN.EMAIL.MAILING_LISTS.ERROR_REQUIRED');
    return;
  }

  isSaving.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    if (selectedList.value) {
      await MailingListsAPI.update(selectedList.value.id, listPayload());
      successMessage.value = t('CAMPAIGN.EMAIL.MAILING_LISTS.SUCCESS_UPDATE');
    } else {
      await MailingListsAPI.create(listPayload());
      successMessage.value = t('CAMPAIGN.EMAIL.MAILING_LISTS.SUCCESS_CREATE');
    }

    resetForm();
    await fetchLists();
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message ||
      t('CAMPAIGN.EMAIL.MAILING_LISTS.ERROR_SAVE');
  } finally {
    isSaving.value = false;
  }
};

const deactivateList = async mailingList => {
  const confirmMessage = t('CAMPAIGN.EMAIL.MAILING_LISTS.CONFIRM_DEACTIVATE', {
    name: mailingList.name,
  });

  // eslint-disable-next-line no-alert
  if (!window.confirm(confirmMessage)) return;

  errorMessage.value = '';
  successMessage.value = '';

  try {
    await MailingListsAPI.delete(mailingList.id);
    successMessage.value = t('CAMPAIGN.EMAIL.MAILING_LISTS.SUCCESS_DEACTIVATE');
    await fetchLists();
    if (selectedList.value?.id === mailingList.id) resetForm();
  } catch (error) {
    errorMessage.value = t('CAMPAIGN.EMAIL.MAILING_LISTS.ERROR_DEACTIVATE');
  }
};

onMounted(fetchLists);
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.EMAIL.MAILING_LISTS.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.EMAIL.MAILING_LISTS.NEW_LIST')"
    @click="resetForm"
  >
    <div class="flex flex-col gap-6">
      <div class="flex items-center justify-between gap-3">
        <router-link
          :to="emailCampaignsRoute"
          class="inline-flex items-center gap-2 text-sm font-medium text-n-blue-11 hover:text-n-blue-12"
        >
          <span class="i-lucide-arrow-left size-4" />
          {{ t('CAMPAIGN.EMAIL.MAILING_LISTS.BACK') }}
        </router-link>
      </div>

      <section class="grid grid-cols-1 gap-4 border-b border-n-weak pb-6">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Input
            v-model="form.name"
            :label="t('CAMPAIGN.EMAIL.MAILING_LISTS.NAME')"
            :placeholder="t('CAMPAIGN.EMAIL.MAILING_LISTS.NAME_PLACEHOLDER')"
          />
          <Input
            :model-value="
              isEditing
                ? t('CAMPAIGN.EMAIL.MAILING_LISTS.MODE_EDIT')
                : t('CAMPAIGN.EMAIL.MAILING_LISTS.MODE_CREATE')
            "
            :label="t('CAMPAIGN.EMAIL.MAILING_LISTS.MODE')"
            disabled
          />
        </div>
        <TextArea
          v-model="form.description"
          :label="t('CAMPAIGN.EMAIL.MAILING_LISTS.DESCRIPTION')"
          :placeholder="
            t('CAMPAIGN.EMAIL.MAILING_LISTS.DESCRIPTION_PLACEHOLDER')
          "
        />
        <TextArea
          v-model="form.terms"
          :label="t('CAMPAIGN.EMAIL.MAILING_LISTS.TERMS')"
          :placeholder="t('CAMPAIGN.EMAIL.MAILING_LISTS.TERMS_PLACEHOLDER')"
        />
        <div class="flex flex-wrap items-center gap-3">
          <Button
            :label="
              isEditing
                ? t('CAMPAIGN.EMAIL.MAILING_LISTS.UPDATE')
                : t('CAMPAIGN.EMAIL.MAILING_LISTS.CREATE')
            "
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="saveList"
          />
          <Button
            v-if="isEditing"
            :label="t('CAMPAIGN.EMAIL.MAILING_LISTS.CANCEL')"
            variant="faded"
            color="slate"
            @click="resetForm"
          />
        </div>
        <p v-if="errorMessage" class="text-sm text-n-ruby-11">
          {{ errorMessage }}
        </p>
        <p v-if="successMessage" class="text-sm text-n-teal-11">
          {{ successMessage }}
        </p>
      </section>

      <div
        v-if="isLoading"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <section v-else class="overflow-hidden rounded-lg border border-n-weak">
        <table class="w-full border-collapse text-left text-sm">
          <thead class="bg-n-alpha-2 text-n-slate-11">
            <tr>
              <th class="px-4 py-3 font-medium">
                {{ t('CAMPAIGN.EMAIL.MAILING_LISTS.TABLE.LIST') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAMPAIGN.EMAIL.MAILING_LISTS.TABLE.STATUS') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAMPAIGN.EMAIL.MAILING_LISTS.TABLE.SUBSCRIBERS') }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{ t('CAMPAIGN.EMAIL.MAILING_LISTS.TABLE.TERMS') }}
              </th>
              <th class="px-4 py-3 font-medium text-right">
                {{ t('CAMPAIGN.EMAIL.MAILING_LISTS.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="mailingList in sortedLists"
              :key="mailingList.id"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-3">
                <div class="font-medium text-n-slate-12">
                  {{ mailingList.name }}
                </div>
                <div class="text-xs text-n-slate-10">
                  {{
                    mailingList.description ||
                    t('CAMPAIGN.EMAIL.MAILING_LISTS.NO_DESCRIPTION')
                  }}
                </div>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ mailingList.status }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ mailingList.subscribers_count || 0 }}
              </td>
              <td class="max-w-sm px-4 py-3 text-n-slate-11">
                <span class="line-clamp-2">
                  {{
                    mailingList.terms ||
                    t('CAMPAIGN.EMAIL.MAILING_LISTS.NO_TERMS')
                  }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-end gap-2">
                  <Button
                    :label="t('CAMPAIGN.EMAIL.MAILING_LISTS.EDIT')"
                    size="sm"
                    variant="faded"
                    color="slate"
                    @click="selectList(mailingList)"
                  />
                  <Button
                    v-if="mailingList.status === 'active'"
                    :label="t('CAMPAIGN.EMAIL.MAILING_LISTS.DEACTIVATE')"
                    size="sm"
                    variant="faded"
                    color="ruby"
                    @click="deactivateList(mailingList)"
                  />
                </div>
              </td>
            </tr>
            <tr v-if="sortedLists.length === 0">
              <td colspan="5" class="px-4 py-8 text-center text-n-slate-11">
                {{ t('CAMPAIGN.EMAIL.MAILING_LISTS.EMPTY') }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  </CampaignLayout>
</template>
